import React, { useContext } from 'react';
import { observer } from 'mobx-react-lite';
import { CircularProgress, Typography, Paper } from '@mui/material';
import { Event as EventIcon } from '@mui/icons-material';
import classnames from 'classnames';
import styled from 'styled-components';

import { t } from '../../../../../../utils/i18next';
import { paymentTypes } from '../../../../../../constants/mixpanel';
import PaymentButtonWrapper from '../../../../general/PaymentButtonWrapper';
import CurrencyFormat from '../../../../general/CurrencyFormat';
import { storeContext } from '../../../../../context';
import { StyledPaymentItemContainer } from '../../../../../theme/styledElements';
import { spacingInRems } from '../../../../../theme/themeUtils';
import { OfferItemPropType } from '../../../../../propTypes';
import ServiceFeeTooltip from './ServiceFeeTooltip';

const translationBase = 'payables.total_side_bar.assist';

const StyledContainer = styled.div`
  && {
    position: relative;
    .offerItem {
      &.hidden {
        opacity: 0;
      }
    }
    .selectMoreBills {
      border-radius: ${spacingInRems()};
      padding: ${spacingInRems()};
      text-align: center;
      background-color: ${({ theme }) => theme.palette.secondary.main};

      & > * {
        color: white;
      }
      .title {
        line-height: ${spacingInRems(2)};
        margin-bottom: ${spacingInRems()};
      }
    }
    .loaderContainer {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

const OfferItemSideBar = observer(props => {
  const {
    payable,
    translations: { currentLanguage }
  } = useContext(storeContext);

  const { ASSIST_PP, FINANCING_PP } = paymentTypes;
  const { selectBills, isLoadingOffer } = payable;
  const buttonType = payable.isFinancedOffer ? FINANCING_PP : ASSIST_PP;

  const { duration, hasValidOffer } = props;

  const getPaymentPlanDurationText = () => (
    <Typography variant="subtitle1" data-testid="payment-plan-duration">
      {t(`${translationBase}.duration`, {
        months: duration
      })}
    </Typography>
  );

  const getSelectMoreBillsMessage = () => (
    <Paper className="selectMoreBills" data-testid="select-more-bills-message">
      <EventIcon />
      <Typography variant="h6" className="title">
        {t(`${translationBase}.manage-bills`)}
      </Typography>
      <Typography className="subTitle">
        {t(`${translationBase}.add-more`)}
      </Typography>
    </Paper>
  );

  return (
    <StyledContainer>
      {selectBills && !hasValidOffer && !isLoadingOffer ? (
        getSelectMoreBillsMessage()
      ) : (
        <StyledPaymentItemContainer
          data-testid="offerItem"
          className={classnames('offerItem', { hidden: isLoadingOffer })}
        >
          <Typography variant="subtitle1" color={'textPrimary'}>
            {t(`${translationBase}.payment_plan`)}
          </Typography>
          <Typography className="amount">
            <CurrencyFormat
              value={Math.max(payable.limitedOfferPaymentAmount, 0)}
            />
          </Typography>
          {getPaymentPlanDurationText()}
          {!!payable.offerServiceFee && (
            <ServiceFeeTooltip serviceFee={payable.offerServiceFee} />
          )}
          <div className="buttonContainer">
            <PaymentButtonWrapper
              color="primary"
              size="large"
              aria-label={t(`${translationBase}.pp_options`)}
              paymentButtonType={buttonType}
            >
              {t(`${translationBase}.pp_options`)}
            </PaymentButtonWrapper>
          </div>
        </StyledPaymentItemContainer>
      )}

      {isLoadingOffer && (
        <div className={'loaderContainer'} data-testid="dynamic-offer-loader">
          <CircularProgress className={'loader'} />
        </div>
      )}
    </StyledContainer>
  );
});

OfferItemSideBar.propTypes = OfferItemPropType;

export default OfferItemSideBar;
